import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogContent, Grid } from '@mui/material'
import { isNil } from 'lodash'
import Input from 'common/components/form/Input'
import Select from 'common/components/form/Select'
import DateTimePicker from 'common/components/form/DateTimePicker'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import { LOT_CATEGORIE, LOT_CATEGORIE_ITEMS, LOT_STATUT, LOT_STATUT_ITEMS } from './DevLotsUtils'
import TicketsPicker from './TicketsPicker'

// Création (lot absent, avec ses tickets existants) ou modification d'un lot DEV Aquaparc.
// onValidate doit renvoyer une promesse : le bouton reste en chargement jusqu'à sa résolution.
const DialogLotDev = ({ lot, allTickets = [], onClose = () => {}, onValidate = () => Promise.resolve() }) => {
    const [form, setForm] = useState(lot ?? { categorie: LOT_CATEGORIE.DEV, statut: LOT_STATUT.A_VENIR })
    const [tickets, setTickets] = useState([])
    const [isSaving, setIsSaving] = useState(false)
    const change = (changes) => setForm(f => ({ ...f, ...changes }))

    const isEdit = !isNil(form.id)
    const isDev = form.categorie === LOT_CATEGORIE.DEV
    const isValid = !!form.nom && (!isDev || !isNil(form.statut)) && (isEdit || tickets.length > 0)

    const changeCategorie = (categorie) => change({
        categorie,
        statut: categorie === LOT_CATEGORIE.DEV ? (form.statut ?? LOT_STATUT.A_VENIR) : undefined,
    })

    const validate = () => {
        const ouverture = form.statut === LOT_STATUT.EN_COURS && isNil(form.ouverture) ? Date.now() : form.ouverture
        setIsSaving(true)
        onValidate({ ...form, ouverture, tickets: isEdit ? form.tickets : tickets }).finally(() => setIsSaving(false))
    }

    return (
        <Dialog open maxWidth='sm' fullWidth>
            <BasicDialogTitle onClose={onClose}>{isEdit ? `Modifier le lot ${lot.nom}` : 'Créer un nouveau lot'}</BasicDialogTitle>
            <DialogContent>
                <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' sx={{ paddingTop: 1 }}>
                    <Grid size={12}>
                        <Input label='Nom du lot' value={form.nom} onChange={v => change({ nom: v })} required />
                    </Grid>
                    <Grid size={6}>
                        <Select label='Catégorie' items={LOT_CATEGORIE_ITEMS} value={form.categorie} onChange={changeCategorie} sortItems={false} />
                    </Grid>
                    {isDev && (
                        <Grid size={6}>
                            <Select label='Statut' items={LOT_STATUT_ITEMS} value={form.statut} onChange={v => change({ statut: v })} sortItems={false} required />
                        </Grid>
                    )}
                    <Grid size={6}>
                        <DateTimePicker label='Échéance' value={form.echeance} onChange={v => change({ echeance: v })} />
                    </Grid>
                    {isEdit && (
                        <Grid size={6}>
                            <DateTimePicker label='Clôture' value={form.cloture} onChange={v => change({ cloture: v })} />
                        </Grid>
                    )}
                    <Grid size={12}>
                        <Input label='Commentaire' value={form.commentaire} onChange={v => change({ commentaire: v })} multiline />
                    </Grid>
                    {!isEdit && (
                        <Grid size={12}>
                            <TicketsPicker tickets={allTickets} values={tickets} onChange={setTickets} obligatory />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={isEdit ? 'Modifier' : 'Créer'}
                onValidate={validate}
                isLoading={isSaving}
                isDisabled={!isValid}
            />
        </Dialog>
    )
}

DialogLotDev.propTypes = {
    lot: PropTypes.shape({ nom: PropTypes.string }),
    allTickets: PropTypes.arrayOf(PropTypes.shape({})),
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

export default DialogLotDev
